import { useState } from "react";
import {
  Text,
  Icon,
  Button,
  Dialog,
  TwSelector as Selector,
  HelperText,
  Link,
} from "@virkesborsen/ui-component-library";
import { formatPriceWithCurrency } from "../../../utils";
import { getTotalForestArea } from "../utils";
import { useSubscription } from "../../../context/SubscriptionCheckoutContext";
import { getSubscriptionCouponDiscount } from "../../../../../api/services/subscription";

export default function OrderSummary() {
  const {
    userProperties,
    addSelectedProperty,
    selectedProperties,
    removeSelectedProperty,
    couponDiscount,
    orderPrices,
    paymentMethod,
    errorMessages,
    setErrorMessages,
    setCouponDiscount,
  } = useSubscription();

  const [isSelectPropertyOpen, setIsSelectPropertyOpen] = useState(false);
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const totalNumberOfProperties = selectedProperties.length;
  const totalForestArea = getTotalForestArea(selectedProperties);
  const firstThreeProperties = selectedProperties.slice(0, 3);
  const remainingProperties = selectedProperties.slice(3).length;
  const hasSelectedProperties = selectedProperties.length > 0;

  const handleSelect = (option) => {
    const selectedProperty = userProperties.find(
      (prop) => prop.ref === option.value
    );
    if (!selectedProperties.some((p) => p.ref === selectedProperty.ref)) {
      addSelectedProperty(selectedProperty);
      setErrorMessages((prev) => ({ ...prev, properties: undefined }));
    } else {
      removeSelectedProperty(selectedProperty);
    }
  };

  const getDiscountLabel = (data) => {
    if (!data) return;

    if (data.coupon_discount_percentage < 5) {
      return `${data.coupon_discount_price} SEK rabatt`;
    }

    return `${data.coupon_discount_percentage}% rabatt`;
  };

  const handleCouponCodeRemove = () => {
    setCouponDiscount(null);
  };
  
    const handleCouponCodeChange = async (e) => {
      const value = e.target.value;
      if (!value || value.length <= 3) return;
   
      const { data, error, message, ok } = await getSubscriptionCouponDiscount({
        refs: selectedProperties.map((prop) => prop.ref),
        coupon_code: value,
      });
  
      if (error) {
        setErrorMessage("Ogiltig rabattkod");
      }
  
      if (ok) {
        setCouponDiscount({
          coupon_code: value.toUpperCase(),
          label: getDiscountLabel(data),
          ...data,
        });
        setErrorMessage(null);
      }
    };

  return (
    <>
      <div className="flex flex-col w-full gap-6">
          <div className="w-full flex flex-col gap-2">
        <div className="w-full flex flex-col gap-4">
          <Text variant="labelLargeMedium" className="pl-4">
            Lägg till rabattkod
          </Text>
          <div className="flex flex-row gap-2 justify-between items-center bg-white-50 rounded-xl p-4 h-14">
            {couponDiscount ? (
              <>
                <div
                  className="bg-cloud-300 flex w-auto items-center justify-center gap-1 rounded-xs px-2 py-1 cursor-pointer hover:bg-cloud-500"
                  onClick={handleCouponCodeRemove}
                >
                  <Text variant="labelSmallMedium">
                    {couponDiscount.coupon_code}
                  </Text>
                  <Icon name="CrossSimple" size="medium" />
                </div>
                <Text variant="bodySmallRegular">{couponDiscount.label}</Text>
              </>
            ) : (
              <input
                type="text"
                placeholder=""
                className="border border-transparent bg-transparent w-full focus:outline-none"
                onChange={handleCouponCodeChange}
              />
            )}
          </div>
        </div>

        {errorMessage && (
          <HelperText
            icon="ExclamationFilled"
            label={errorMessage}
            className={"text-danger-500 fill-danger-500 pl-4"}
          >
            {errorMessage || " "}
          </HelperText>
        )}
      </div>
        <div className="flex flex-col w-full gap-4">
          <div className="flex flex-col gap-4">
            <Text variant="text-base/leading-normal/medium" className="pl-4">
              Sammanfattning
            </Text>
            <div className="w-full flex flex-col bg-white-50 rounded-xl p-4 gap-4">
              <div className="flex flex-row gap-2 justify-start items-start">
                <Icon
                  tw
                  name="MapPinSimple"
                  size="large"
                  spacing="medium"
                  className="fill-brown-700 bg-stone-200"
                />
                <div className="flex flex-col flex-1 h-full justify-center">
                  <div className="flex flex-col gap-1">
                    <Text variant="text-sm/leading-normal/medium">
                      {hasSelectedProperties
                        ? `Fastighet (${totalNumberOfProperties})`
                        : "Välj fastigheter att aktivera plus på"}
                    </Text>
                    {hasSelectedProperties && (
                      <Text
                        variant="text-sm/leading-normal/normal"
                        className="text-cloud-900 mb-3"
                      >
                        {totalForestArea} hektar
                      </Text>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    {firstThreeProperties.map((property) => (
                      <Text
                        variant="text-sm/leading-normal/normal"
                        className="text-pebble-500"
                      >
                        {property.designation}
                      </Text>
                    ))}
                    {remainingProperties > 0 && (
                      <Text
                        variant="text-sm/leading-normal/normal"
                        className="text-pebble-500"
                      >
                        +{remainingProperties} till
                      </Text>
                    )}
                  </div>
                </div>
                <Button
                  tw
                  variant="secondary"
                  size="small"
                  onClick={() => setIsSelectPropertyOpen(true)}
                >
                  {hasSelectedProperties ? "Ändra" : "Lägg till"}
                </Button>
              </div>
              {hasSelectedProperties && (
                <>
                  <div className="w-full h-[0.5px] border border-stone-500/17" />

                  <div className="flex flex-row gap-2 justify-start items-start">
                    <div className="flex flex-col gap-1">
                      <Text variant="text-sm/leading-normal/medium">
                        Treebula Plus
                      </Text>
                    </div>
                  </div>

                  <div className="w-full h-[0.5px] border border-stone-500/17" />

                  {/* Total price */}
                  <div className="flex flex-row gap-2 justify-between items-center">
                    <div className="flex flex-col gap-1">
                      <Text variant="text-sm/leading-normal/medium">
                        Totalbelopp
                      </Text>
                      <Text
                        variant="text-sm/leading-normal/normal"
                        className="text-pebble-500"
                      >
                        {formatPriceWithCurrency(orderPrices.discountPrice)}
                      </Text>
                    </div>
                    <Button
                      tw
                      variant="secondary"
                      size="small"
                      onClick={() => setIsOrderDetailsOpen(true)}
                    >
                      Detaljer
                    </Button>
                  </div>
                </>
              )}
            </div>
            {errorMessages.properties && (
              <HelperText
                icon="ExclamationFilled"
                label={errorMessages.properties}
                className={"text-danger-500 fill-danger-500 pl-4 pt-1-5"}
              />
            )}
          </div>
        </div>

        {hasSelectedProperties && (
          <>
            <div className="w-full h-[0.5px] border border-stone-500/17" />

            <div className="flex flex-col w-full gap-4">
              <Text variant="text-base/leading-normal/medium">
                Betalningsmetod
              </Text>
              <div className="w-full flex flex-col bg-white-50 rounded-xl p-4 gap-4">
                {paymentMethod === "klarna" && (
                  <div className="flex flex-col gap-4">
                    <img
                      alt="Klarna logo"
                      width="60px"
                      height="24px"
                      src={`${Window.static_path}img/payment-provider/klarna.png`}
                    />
                    <div className="flex flex-row gap-2 bg-stone-100 rounded-md p-3">
                      <Icon
                        name="InfoFilled"
                        size="medium"
                        className="fill-pebble-500"
                      />
                      <div className="flex flex-col gap-2">
                        <Text
                          variant="text-xs/leading-normal/normal"
                          className="text-pebble-500"
                        >
                          Köp via Klarna visas med 25% moms inkluderad. Som
                          företag kan du ändå göra fullt momsavdrag även om det
                          inte syns vid checkout.
                          <br />
                          Du kan klicka på <b>Detaljer</b> ovan för att se mer
                          information.
                        </Text>
                      </div>
                    </div>

                    <Text variant="text-xs/leading-normal/normal">
                      Läs mer hos{" "}
                      <Link
                        tw
                        href="https://www.klarna.com/se/"
                        target="blank"
                        underline
                      >
                        Klarna.com/se
                      </Link>
                      {", "}
                      <Link
                        tw
                        href="https://www.klarna.com/se/villkor/"
                        target="blank"
                        underline
                      >
                        Klarnas köpvillkor
                      </Link>
                      {", "}
                      <Link
                        tw
                        href="https://www.klarna.com/se/dataskydd/"
                        target="blank"
                        underline
                      >
                        Klarnas sekretessmeddelande
                      </Link>
                      {" och "}
                      <Link
                        tw
                        href="https://www.klarna.com/se/cookies/"
                        target="blank"
                        underline
                      >
                        Klarnas cookie-meddelande
                      </Link>
                    </Text>

                    <div className="w-full h-[0.5px] border border-stone-500/17" />

                    <div className="flex flex-row gap-2">
                      <Text variant="text-xs/leading-normal/normal">
                        <Link
                          tw
                          href="https://treebula.se/terms/"
                          target="blank"
                          underline
                        >
                          Treebulas användarvillkor
                        </Link>
                        {", "}
                        <Link
                          tw
                          href="https://treebula.se/privacy/"
                          target="blank"
                          underline
                        >
                          Treebulas integritetspolicy
                        </Link>
                        .
                      </Text>
                    </div>
                  </div>
                )}
                {paymentMethod === "invoice" && (
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-row py-1 px-2 bg-charcoal-500 text-white-50 rounded-md w-fit">
                      <Text variant="text-xs/leading-normal/medium">
                        Faktura
                      </Text>
                    </div>
                    <div className="flex flex-row gap-2 bg-stone-100 rounded-md p-3">
                      <Icon
                        name="InfoFilled"
                        size="medium"
                        className="fill-pebble-500"
                      />
                      <div className="flex flex-col gap-2">
                        <Text
                          variant="text-xs/leading-normal/normal"
                          className="text-pebble-500"
                        >
                          Belopp över 20 000 kr betalas via faktura. Vi
                          kontaktar dig inom kort
                        </Text>
                      </div>
                    </div>

                    <div className="w-full h-[0.5px] border border-stone-500/17" />

                    <div className="flex flex-row gap-2">
                      <Text variant="text-xs/leading-normal/normal">
                        <Link
                          tw
                          href="https://treebula.se/terms/"
                          target="blank"
                          underline
                        >
                          Treebulas användarvillkor
                        </Link>
                        {", "}
                        <Link
                          tw
                          href="https://treebula.se/privacy/"
                          target="blank"
                          underline
                        >
                          Treebulas integritetspolicy
                        </Link>
                        .
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Select Property Dialog */}
      <Dialog isOpen={isSelectPropertyOpen} size="medium">
        <Dialog.Header>
          <div className="flex w-full justify-between">
            <Text variant="text-base/leading-normal/medium">
              Välj fastighet
            </Text>
            <Button
              tw
              variant="secondary"
              size="icon"
              onClick={() => setIsSelectPropertyOpen(false)}
            >
              <Icon
                tw
                name="CrossSimple"
                className="color-charcoal-500"
                size="medium"
              />
            </Button>
          </div>
        </Dialog.Header>
        <Dialog.Content>
          <Selector>
            {userProperties?.map((option) => (
              <Selector.Option
                key={option.ref}
                label={`${option.designation} ${
                  option.type !== "PROP" ? "(Ej fastighet)" : ""
                }`}
                value={option.ref}
                isSelected={selectedProperties?.some(
                  (p) => p.ref === option.ref
                )}
                onClick={handleSelect}
                disabled={option.type !== "PROP"}
              />
            ))}
          </Selector>
        </Dialog.Content>
        <Dialog.Footer>
          <Button
            tw
            variant="primary"
            onClick={() => setIsSelectPropertyOpen(false)}
          >
            Klar
          </Button>
        </Dialog.Footer>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog isOpen={isOrderDetailsOpen} size="small">
        <Dialog.Header>
          <div className="flex w-full justify-between">
            <Text variant="text-base/leading-normal/medium">Prisdetaljer</Text>
            <Button
              tw
              variant="secondary"
              size="icon"
              onClick={() => setIsOrderDetailsOpen(false)}
            >
              <Icon
                tw
                name="CrossSimple"
                className="color-charcoal-500"
                size="medium"
              />
            </Button>
          </div>
        </Dialog.Header>
        <Dialog.Content>
          <div className="flex flex-col gap-4 h-full">
            <div className="flex flex-col gap-4">
              <div className="flex flex-row gap-2 justify-between items-center">
                <Text variant="text-sm/leading-normal/normal">
                  Treebula Plus {orderPrices.discountPrice} kr
                </Text>
                <Text variant="text-sm/leading-normal/medium">
                  {formatPriceWithCurrency(orderPrices.propertiesTotalPrice)}
                </Text>
              </div>

              <div className="w-full h-[0.5px] border border-stone-500/17" />

              {couponDiscount && (
                <div className="flex flex-row gap-2 justify-between items-center">
                  <Text variant="text-sm/leading-normal/normal">
                    Rabattkod {couponDiscount.coupon_code}
                  </Text>
                  <Text variant="text-sm/leading-normal/medium">
                    -
                    {formatPriceWithCurrency(
                      couponDiscount.coupon_discount_price
                    )}
                  </Text>
                </div>
              )}
              <div className="flex flex-row gap-2 justify-between items-center">
                <Text
                  variant="text-sm/leading-normal/normal"
                  className="text-charcoal-300"
                >
                  Moms
                </Text>
                <Text variant="text-sm/leading-normal/medium">
                  {formatPriceWithCurrency(orderPrices.tax)}
                </Text>
              </div>
              <div className="flex flex-row gap-2 justify-between items-center">
                <Text
                  variant="text-sm/leading-normal/normal"
                  className="text-charcoal-300"
                >
                  Inkl. moms
                </Text>
                <Text variant="text-sm/leading-normal/medium">
                  {formatPriceWithCurrency(
                    orderPrices.discountPrice + orderPrices.tax
                  )}
                </Text>
              </div>
            </div>

            <div className="w-full h-[0.5px] border border-stone-500/17 mt-auto" />

            <div className="flex flex-row gap-2 justify-between items-center">
              <Text variant="text-sm/leading-normal/medium">Totalt</Text>
              <div className="flex flex-col gap-1 items-end">
                <Text variant="text-sm/leading-normal/medium">
                  {formatPriceWithCurrency(orderPrices.discountPrice)}
                </Text>
                <Text
                  variant="text-sm/leading-normal/normal"
                  className="text-charcoal-300"
                >
                  exkl. moms
                </Text>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog>
    </>
  );
}
